const RELEASE_PATTERN = /^[a-zA-Z0-9_-]{1,128}$/;

interface ReleaseReloadOptions {
  releaseId: string;
  fetchRelease: () => Promise<unknown>;
  replace: (url: string) => void;
  href: () => string;
  notify: (reload: () => void, reason: "release" | "chunk") => void;
}

export async function fetchCurrentRelease(): Promise<unknown> {
  const response = await fetch(`/release?check=${Date.now()}`, {
    cache: "no-store",
    credentials: "omit",
    signal: AbortSignal.timeout(10_000),
  });
  if (!response.ok) throw new Error("Release check failed");
  return response.json();
}

export function startReleaseReload(options: ReleaseReloadOptions) {
  let checking = false;
  let stopped = false;
  let reloading = false;
  let notified = false;
  let pendingRelease: string | undefined;

  const manualReload = () => {
    if (stopped || reloading) return;
    const url = new URL(options.href());
    if (pendingRelease) url.searchParams.set("_verne_release", pendingRelease);
    reloading = true;
    options.replace(url.href);
  };

  const check = async () => {
    if (stopped || checking || reloading || document.visibilityState === "hidden" || !navigator.onLine) {
      return;
    }
    checking = true;
    try {
      const result = await options.fetchRelease();
      if (stopped || !result || typeof result !== "object" || !("releaseId" in result)) {
        return;
      }
      const releaseId = result.releaseId;
      if (typeof releaseId !== "string" || !RELEASE_PATTERN.test(releaseId)) {
        return;
      }
      pendingRelease = releaseId === options.releaseId ? undefined : releaseId;
      if (pendingRelease && !notified) {
        notified = true;
        options.notify(manualReload, "release");
      }
    } catch {
      // An offline tab or a deployment outage is not evidence of a new release.
    } finally {
      checking = false;
    }
  };

  document.addEventListener("visibilitychange", check);
  window.addEventListener("online", check);
  const timer = window.setInterval(check, 60_000);

  return {
    check,
    async recoverChunkError() {
      // Show recovery even when a check is already running, offline, or fails.
      if (stopped || reloading) return;
      notified = true;
      options.notify(manualReload, "chunk");
      await check();
    },
    stop() {
      stopped = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", check);
      window.removeEventListener("online", check);
    },
  };
}
